// router/index.js
import Vue from "vue";
import Router from "vue-router";
import LoginPage from "@/components/LoginPage.vue";
import FirstPage from "@/components/FirstPage.vue";
import PressPage from "@/components/PressPage.vue";
import anonymizeDecision from "@/components/anonymizeDecision.vue";
import About from "@/components/about.vue";
import TrialMonitoring from "@/components/TrialMonitoring.vue";
import PolitiqueDonnees from '@/components/PolitiqueDonnees.vue';
import ForgotPassword from '@/components/ForgotPassword.vue';
import InviteCreator from "@/components/InviteCreator.vue";
import AdminConsolePage from "@/components/AdminConsolePage.vue";
import AdminDashboard from "@/components/AdminDashboard.vue";
import ActivityPage from "@/components/ActivityPage.vue";
import MetricsPage from "@/components/MetricsPage.vue";
import FeatureUsagePage from "@/components/FeatureUsagePage.vue";
import CoSellBriefPage from "@/components/CoSellBriefPage.vue";
import ConditionsUtilisation from '@/components/ConditionsUtilisation.vue';
import ConditionsVente from '@/components/ConditionsVente.vue';
import ResetPassword from '@/components/ResetPassword.vue';
import RecentSignups from "@/components/RecentSignups.vue";
import FeedbackMonitoring from "@/components/FeedbackMonitoring.vue";
import DossierCostBreakdown from "@/components/DossierCostBreakdown.vue";
import EmailVerificationAlert from "@/components/EmailVerificationAlert.vue";
import EmailVerified from "@/components/VerifiedEmail.vue";
import SuccessPage from "@/components/SuccessPage.vue";
import CancelPage from "@/components/CancelPage.vue";
import UsageGuide from "@/components/usageGuide.vue";
import MentionsPage from "@/components/mentionsPage.vue";
import DashboardPage from "@/components/dashboardPage.vue";
import SignupWithTokenPage from "@/components/SignupWithTokenPage.vue";
import ContactPage from "@/components/contactPage.vue";
import EuropePage from "@/components/EuropePage.vue";
import DossierPage from '@/components/DossierPage.vue';
import AgentPage from '@/components/AgentPage.vue';
import PresentationPage from "@/components/PresentationPage.vue";
import OAuthCallback from "@/components/OAuthCallback.vue";
import StudentPage from "@/components/StudentPage.vue";
import IAPage from "@/components/IAPage.vue";
import SourcesCouverturePage from "@/components/SourcesCouverturePage.vue";
import FAQPage from "@/components/FAQPage.vue";
import ChangePassword from "@/components/ChangePassword.vue";
import FlitPartnershipPage from "@/components/FlitPartnershipPage.vue";
import LandingContactPage from "@/components/LandingContactPage.vue";
import LandingLexroomPage from "@/components/LandingLexroomPage.vue";
import { getSubscriptionCached, getOwnRoles } from "@/services/api";
import { supabase } from "@/supabase/supabaseClient";
import { getOwnProfile } from "@/supabase/profile";
import CabAdminLayout from "@/components/cabadmin/CabAdminLayout.vue";
import CabAdminUsers from "@/components/cabadmin/CabAdminUsers.vue";
import CabAdminUsage from "@/components/cabadmin/CabAdminUsage.vue";

Vue.use(Router);

// ============================================================
// CONFIGURATION
// ============================================================

// Routes publiques (pas besoin d'être connecté)
const PUBLIC_ROUTES = [
  'home', 'login', 'ForgotPassword', 'ResetPassword',
  'ConditionsUtilisation', 'ConditionsVente', 'PolitiqueDonnees',
  'press', 'presentation', 'Europe', 'contact', 'ia', 'sources',
  'faq', 'flitPartnership', 'flitContact', 'landingLexroom', 'mentions', 'usageGuide',
  'verifyEmail', 'verifiedEmail', 'oauth-callback', 'signuptoken',
  'anonymizeDecision',
  'first', 'success', 'cancel'
];

// Pages d'authentification (redirect vers dashboard si déjà connecté)
const AUTH_PAGES = ['home', 'login', 'ForgotPassword'];

// Essai gratuit en libre-service désactivé : l'accès passe par une démo.
// Les anciennes URL d'inscription (Google Ads, blog, e-mails) arrivent sur
// l'accueil avec le formulaire de démo ouvert, en gardant les paramètres UTM.
const toDemoForm = to => ({ path: '/', query: { ...to.query, demo: '1' } });

// ============================================================
// HELPER UNIQUE : checkSession avec timeout
// ============================================================

async function checkSession(timeoutMs = 3000) {
  try {
    const result = await Promise.race([
      supabase.auth.getSession(),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Session timeout')), timeoutMs)
      )
    ]);

    if (result.error) {
      return { session: null, shouldCleanup: true };
    }

    return { session: result.data?.session || null, shouldCleanup: false };
  } catch (error) {
    return { session: null, shouldCleanup: true };
  }
}

function cleanupCorruptedSession() {
  localStorage.removeItem('sb-queryjuriste-auth');
}

// ============================================================
// FONCTION SSO - Appelle le backend pour assigner le cabinet
// ============================================================

const API_URL = process.env.VUE_APP_API_URL || 'https://api.query-juriste.com';

// eslint-disable-next-line no-unused-vars
async function ensureSSOUserProfile(user) {

  try {
    // Récupérer le token d'accès
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token;

    if (!accessToken) {
      console.error('🔐 [ROUTER] ❌ No access token available');
      return;
    }

    // Appeler le backend pour assigner le cabinet SSO (utilise service_role, bypass RLS)
    const response = await fetch(`${API_URL}/copilot/sso-assign-cabinet`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }
    });

    const result = await response.json();

    if (result.assigned) {
      // Cabinet assigned successfully
    } else {
      // Other reason - continue without blocking
    }


  } catch (err) {
    console.error('🔐 [ROUTER] ❌ Error in ensureSSOUserProfile:', err);
    // Ne pas bloquer la connexion si l'assignation échoue
  }
}

// ============================================================
// ROUTES
// ============================================================

const router = new Router({
  mode: "history",
  routes: [
    {
      path: "/",
      name: "home",
      component: About,
      alias: ["/about"],
      meta: {
        title: "Query Juriste – Accueil",
        description: "Construisez votre argumentaire avec l'IA"
      }
    },
    {
      path: '/invites',
      name: 'inviteCreator',
      component: InviteCreator,
      meta: { requiresAuth: true }
    },
    {
      path: '/admin/acces',
      name: 'accessManager',
      component: () => import('@/components/admin/AccessManagerPage.vue'),
      meta: { requiresAuth: true, requiresInvitePermission: true }
    },
    {
      path: '/admin/devis',
      name: 'quoteBuilder',
      component: () => import('@/components/admin/QuoteBuilderPage.vue'),
      meta: { requiresAuth: true, requiresInvitePermission: true }
    },
    {
      path: '/admin/clients-ville',
      name: 'clientCities',
      component: () => import('@/components/admin/ClientCitiesPage.vue'),
      meta: { requiresAuth: true, requiresInvitePermission: true }
    },
    {
      path: '/admin/suivi-usage',
      name: 'csmUsage',
      component: () => import('@/components/admin/CsmUsagePage.vue'),
      meta: { requiresAuth: true, requiresInvitePermission: true }
    },
    {
      path: '/admin/devis-suivi',
      name: 'quotesDashboard',
      component: () => import('@/components/admin/QuotesDashboardPage.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin-dashboard',
      name: 'adminDashboard',
      component: AdminDashboard,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/trial-monitoring',
      name: 'trialMonitoring',
      component: TrialMonitoring,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/recent-signups',
      name: 'recentSignups',
      component: RecentSignups,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/activity',
      name: 'activityPage',
      component: ActivityPage,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/metrics',
      name: 'metricsPage',
      component: MetricsPage,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/feature-usage',
      name: 'featureUsage',
      component: FeatureUsagePage,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin/cosell',
      name: 'cosellBrief',
      component: CoSellBriefPage,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/feedback-monitoring',
      name: 'feedbackMonitoring',
      component: FeedbackMonitoring,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/admin-dashboard/pipelines',
      name: 'pipelinesAdmin',
      component: () => import('@/components/admin/PipelinesPage.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/dossier-cost/:dossierId?',
      name: 'dossierCostBreakdown',
      component: DossierCostBreakdown,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: "/auth/callback",
      name: "oauth-callback",
      component: OAuthCallback
    },
    {
      path: '/conditions-utilisation',
      name: 'ConditionsUtilisation',
      component: ConditionsUtilisation,
      meta: { title: 'Conditions Générales d\'Utilisation - Query Juriste' }
    },
    {
      path: '/admin',
      name: 'adminConsole',
      component: AdminConsolePage,
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/conditions-vente',
      name: 'ConditionsVente',
      component: ConditionsVente,
      meta: { title: 'Conditions Générales de Vente - Query Juriste' }
    },
    { path: "/trialPage", redirect: toDemoForm },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword,
    },
    {
      path: '/change-password',
      name: 'ChangePassword',
      component: ChangePassword,
      meta: { requiresAuth: true }
    },
    {
      path: "/anonymizeDecision",
      name: "anonymizeDecision",
      component: anonymizeDecision
    },
    {
      path: '/politique-donnees',
      name: 'PolitiqueDonnees',
      component: PolitiqueDonnees,
    },
    {
      path: "/dashboardPage",
      name: "dashboard",
      component: DashboardPage,
      meta: { requiresAuth: true }
    },
    {
      path: "/studentPage",
      name: "student",
      component: StudentPage,
      meta: { requiresAuth: true }
    },
    { path: "/loginPage", name: "login", component: LoginPage },
    { path: "/mentionsPage", name: "mentions", component: MentionsPage },
    { path: "/VerifiedEmail", name: "verifiedEmail", component: EmailVerified },
    {
      path: '/dossier/:dossierId',
      name: 'DossierPage',
      component: DossierPage,
      meta: { requiresAuth: true }
    },
    {
      path: '/agent/:dossierId',
      name: 'AgentPage',
      component: AgentPage,
      meta: { requiresAuth: true }
    },
    { path: "/PresentationPage", name: "presentation", component: PresentationPage },
    { path: "/EuropePage", name: "Europe", component: EuropePage },
    { path: "/contactPage", name: "contact", component: ContactPage },
    { path: "/signinPage", redirect: toDemoForm },
    { path: "/essai-4-semaines", redirect: toDemoForm },
    { path: "/signupWithTokenPage", name: "signuptoken", component: SignupWithTokenPage },
    { path: "/firstPage", name: "first", component: FirstPage },
    { path: "/PressPage", name: "press", component: PressPage },
    {
      path: "/ia",
      name: "ia",
      component: IAPage,
      meta: {
        title: "Ce que Query Juriste fait - et ne fait pas",
        description: "Découvrez ce que Query Juriste fait et ne fait pas."
      }
    },
    {
      path: "/sources-jurisprudentielles",
      name: "sources",
      component: SourcesCouverturePage,
      meta: {
        title: "Sources & couverture - Query Juriste",
        description: "Découvrez la couverture complète de la jurisprudence."
      }
    },
    {
      path: "/faq",
      name: "faq",
      component: FAQPage,
      meta: { title: "FAQ - Query Juriste" }
    },
    {
      path: "/partenariat-flit",
      name: "flitPartnership",
      component: FlitPartnershipPage,
      meta: { title: "Partenariat Flit - Query Juriste" }
    },
    {
      path: "/contact-flit",
      name: "flitContact",
      component: LandingContactPage,
      meta: { title: "Contact - Query Juriste" }
    },
    {
      path: "/landing-lexroom",
      name: "landingLexroom",
      component: LandingLexroomPage,
      meta: { title: "Query Juriste : l'IA juridique qui comprend le droit" }
    },
    { path: "/usageGuide", name: "usageGuide", component: UsageGuide },
    { path: '/forgot-password', name: 'ForgotPassword', component: ForgotPassword },
    { path: "/verify-email", name: "verifyEmail", component: EmailVerificationAlert },
    { path: "/success", name: "success", component: SuccessPage },
    { path: "/cancel", name: "cancel", component: CancelPage },
    {
      path: '/cabadmin',
      component: CabAdminLayout,
      meta: { requiresAuth: true, requiresCabAdmin: true },
      children: [
        { path: '', redirect: { name: 'cabadmin-usage' } },
        { path: 'usage', name: 'cabadmin-usage', component: CabAdminUsage },
        { path: 'users', name: 'cabadmin-users', component: CabAdminUsers }
      ]
    }
  ]
});

// ============================================================
// GUARD UNIQUE : Toute la logique de navigation en un seul endroit
// ============================================================

router.beforeEach(async (to, from, next) => {
  try {
    // 1. BLOG REDIRECT (synchrone, prioritaire)
    if (/^(\/blog|\/actualite|\/news|\/articles)(\/|$)/.test(to.path)) {
      window.location.href = to.fullPath;
      return; // Stop ici, pas de next()
    }

    // 2. OAUTH CALLBACK avec access_token dans le hash
    if (to.hash.includes('access_token') && !to.hash.includes('type=recovery')) {
      const { session } = await checkSession();
      if (session?.user) {
        await ensureSSOUserProfile(session.user);
        return next({ name: 'dashboard' });
      }
      return next();
    }

    // 3. ROUTES PUBLIQUES - Pas besoin de vérifier la session
    if (PUBLIC_ROUTES.includes(to.name)) {
      // Sauf si c'est une page d'auth et l'user est déjà connecté
      if (AUTH_PAGES.includes(to.name)) {
        const { session } = await checkSession();
        if (session?.user?.email_confirmed_at) {
          return next({ name: 'dashboard' });
        }
      }
      return next();
    }

    // 4. ROUTES PROTÉGÉES - Vérification obligatoire
    const { session, shouldCleanup } = await checkSession();

    if (shouldCleanup) {
      cleanupCorruptedSession();
    }

    const user = session?.user || null;
    const isAuthenticated = !!user;
    const isVerified = !!user?.email_confirmed_at;


    // Pas de session → Login
    if (!isAuthenticated) {
      return next('/loginPage');
    }

    // Session mais email non vérifié
    if (!isVerified && to.name !== 'verifyEmail') {
      // Exception pour signup avec token
      if (from.path === '/signupWithTokenPage') {
        return next();
      }
      return next('/verify-email');
    }

    // 5. HOME REDIRECT pour users connectés et vérifiés
    if (to.path === '/' && isAuthenticated && isVerified) {
      return next({ name: 'dashboard' });
    }

    // 6. Vérification du paiement si requis
    const requiresPayment = to.matched.some(r => r.meta.requiresPayment);
    if (requiresPayment) {
      try {
        const subscription = await getSubscriptionCached();
        const isPaid = ['student', 'premium', 'lifetime_free'].includes(subscription?.plan) &&
          subscription?.status === 'active';
        if (!isPaid) {
          return next({ name: 'subscription' });
        }
      } catch {
        return next({ name: 'subscription' });
      }
    }

    // 6b. Vérification du rôle cab_admin (DSI cabinet)
    const requiresCabAdmin = to.matched.some(r => r.meta.requiresCabAdmin);
    if (requiresCabAdmin) {
      try {
        const profile = await getOwnProfile();
        if (!profile?.is_cab_admin || !profile?.cabinet_id || !profile?.admin_view_enabled) {
          return next({ name: 'dashboard' });
        }
      } catch (e) {
        console.error('[guard] cab_admin check failed:', e);
        return next({ name: 'dashboard' });
      }
    }

    // 6c. Vérification du rôle admin (console interne Query Juriste)
    // Le backend reste la source de vérité (verify_admin_access sur chaque
    // route /copilot/admin/*) : ce garde-fou évite juste d'ouvrir une console
    // vide et de laisser fuiter la structure de l'UI admin.
    const requiresAdmin = to.matched.some(r => r.meta.requiresAdmin);
    if (requiresAdmin) {
      try {
        const roles = await getOwnRoles();
        if (!roles?.is_admin) {
          return next({ name: 'dashboard' });
        }
      } catch (e) {
        console.error('[guard] admin check failed:', e);
        return next({ name: 'dashboard' });
      }
    }

    // 6d. Gestion des accès : mêmes droits que la création d'invitations
    // (is_admin ou can_create_invites). Le backend revérifie sur chaque appel.
    const requiresInvitePermission = to.matched.some(r => r.meta.requiresInvitePermission);
    if (requiresInvitePermission) {
      try {
        const roles = await getOwnRoles();
        if (!roles?.is_admin && !roles?.can_create_invites) {
          return next({ name: 'dashboard' });
        }
      } catch (e) {
        console.error('[guard] invite permission check failed:', e);
        return next({ name: 'dashboard' });
      }
    }

    // 7. TOUT OK
    next();

  } catch (error) {
    console.error('Router guard error:', error);
    return next('/loginPage');
  }
});

// Debug en développement
if (process.env.NODE_ENV === 'development') {
  window.router = router;
}

export default router;
